import { useState, useRef } from 'react'
import { PDFDocument } from 'pdf-lib'
import { Upload, Download, FileText, Image as ImageIcon } from 'lucide-react'
import Card from '../components/Card'
import Button from '../components/Button'

export default function PDFToImage() {
  const [file, setFile] = useState(null)
  const [images, setImages] = useState([])
  const [processing, setProcessing] = useState(false)
  const [format, setFormat] = useState('png')
  const fileInputRef = useRef(null)

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files?.[0]
    if (selectedFile && selectedFile.type === 'application/pdf') {
      setFile(selectedFile)
      setImages([])
    } else if (selectedFile) {
      alert('Please select a PDF file')
    }
  }

  const convertToImages = async () => {
    if (!file) {
      alert('Please select a PDF file')
      return
    }

    setProcessing(true)
    try {
      // Note: pdf-lib doesn't support rendering PDFs to images directly
      // This would require pdf.js or a canvas-based approach
      // For now, we'll show a message that this requires additional setup
      alert('PDF to Image conversion requires additional libraries. This feature is being enhanced.')
      
      // Placeholder: In a real implementation, you would use pdf.js to render pages to canvas
      // then convert canvas to image
      setImages([])
    } catch (error) {
      alert('Error converting PDF to images: ' + error.message)
    } finally {
      setProcessing(false)
    }
  }

  const downloadImage = (index) => {
    const image = images[index]
    const link = document.createElement('a')
    link.href = image.url
    link.download = `page-${index + 1}.${format}`
    link.click()
  }

  return (
    <div className="space-y-6">
      <Card>
        <Card.Body>
          <div className="mb-6">
            <label className="block text-gray-700 font-semibold mb-3">Select PDF File:</label>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf"
              onChange={handleFileSelect}
              className="hidden"
              id="pdf-upload"
            />
            <Button
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              type="button"
            >
              <Upload className="w-5 h-5 mr-2" />
              {file ? 'Change PDF' : 'Upload PDF'}
            </Button>
            {file && (
              <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-red-600" />
                  <span className="text-sm font-medium text-gray-900">{file.name}</span>
                </div>
              </div>
            )}
          </div>

          <div className="mb-6">
            <label className="block text-gray-700 font-semibold mb-3">Image Format:</label>
            <select
              value={format}
              onChange={(e) => setFormat(e.target.value)}
              className="input w-full"
            >
              <option value="png">PNG</option>
              <option value="jpg">JPG</option>
            </select>
          </div>

          <Button
            onClick={convertToImages}
            variant="primary"
            className="w-full"
            disabled={!file || processing}
          >
            {processing ? 'Converting...' : 'Convert to Images'}
          </Button>
        </Card.Body>
      </Card>

      {images.length > 0 && (
        <Card>
          <Card.Body>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Converted Images:</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {images.map((image, index) => (
                <div key={index} className="space-y-2">
                  <img src={image.url} alt={`Page ${index + 1}`} className="w-full border rounded" />
                  <Button
                    onClick={() => downloadImage(index)}
                    variant="outline"
                    size="sm"
                    className="w-full"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </Button>
                </div>
              ))}
            </div>
          </Card.Body>
        </Card>
      )}

      {!file && (
        <Card>
          <Card.Body className="text-center py-12">
            <ImageIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Upload a PDF file to convert pages to images</p>
          </Card.Body>
        </Card>
      )}
    </div>
  )
}

